// The circle's half of a gesture's live frame (SPEC-viewfinder §4.1).
//
// It listens to `src/state/gesture.ts` and NEVER to `derived.subscribe` (§1.1,
// rule 2): `./camera.ts` measures a frame with the pure `remeasure` and pushes
// it, and `viewState` hears nothing at all until the camera is at rest. Two
// things change under the finger and no others — which items are beyond the
// ring, and which marks have entered it.
//
// Its own module because `wheel.ts` is ratcheted at 661 lines and the ratchet
// only goes down (ADR-0015): „a file that needs more room needs a split".
import type { Derived } from '../derive/derive.ts';
import { markDelta } from '../derive/remeasure.ts';
import type { Item } from '../shared/model.ts';
import { gesture } from '../state/gesture.ts';
import type { ViewState } from '../state/viewstate.ts';
import { type Project, syncBeyond } from './beyond.ts';
import { dotElement, markButton } from './marks-dom.ts';
import { markContext, marksFor } from './marks.ts';
import { measureMark } from './place.ts';

/** What the live half needs of the wheel. Everything measured is a function. */
export type LiveDeps = {
  marks: HTMLElement;
  beyonds: HTMLElement;
  project: Project;
  /** The camera's centre right now — read off the map, never remembered (§1.6, #279). */
  centre: () => { lat: number; lon: number };
  /** What the last full paint settled on, or `null` before the first one. */
  placed: () => { view: ViewState; dial: boolean } | null;
  /** The standing derivation's axis, or `null` before the first snapshot. */
  axis: () => Derived | null;
  disc: () => number;
};

export function hearGesture(deps: LiveDeps): void {
  // What the two layers are holding, carried BETWEEN frames rather than read
  // off the DOM on each one.
  //
  // This is the fix for the largest single cost the review measured in this
  // session. Both layers were being scanned every 150 ms — `dataset.item` off
  // every drawn mark, which at a 5 km radius is every vehicle in the city, and
  // off every dot — and `DOMStringMap` is not a cheap read. With the wheel's
  // half of the live frame switched off the phone measured 59,8 fps with 4
  // frames over 32 ms; with it on and scanning, 55,7 fps with 28.
  //
  // Seeded from the DOM once per gesture, because the settle rebuilds both
  // layers from the derivation and this must not hold elements that are gone.
  let dots: Map<string, HTMLElement> | null = null;
  let marks: Map<string, HTMLElement> | null = null;

  gesture.subscribe((live) => {
    const placed = deps.placed();
    const axis = deps.axis();
    // A settle pushes `null`: `chrome()` is about to rebuild everything from
    // the settled derivation, so there is nothing here to do — and what we were
    // holding is about to stop existing. The dial draws no dots and no marks at
    // all (§6, D-9).
    if (!live || !placed || !axis || placed.dial || deps.disc() === 0) {
      dots = null;
      marks = null;
      return;
    }
    dots ??= held(deps.beyonds);
    marks ??= held(deps.marks);

    // The dots follow the ring in the same frame it passes over them, so an
    // item never reads as both a dot and a mark (§3).
    syncBeyond(deps.beyonds, live.beyond, deps.project, dots);

    const { enter, leave } = markDelta(axis, deps.centre(), placed.view.radius, marks);
    for (const id of leave) {
      marks.get(id)?.remove();
      marks.delete(id);
    }
    // A box nobody has measured cannot be clamped, so each newcomer is measured
    // — and ONLY the newcomer. Measuring the whole layer here was this
    // session's own worst frame: at a wide radius the ring passes over
    // something on most frames of a pan, so a read of every drawn box was
    // happening seven times a second at 6× CPU (§4.3, the review's numbers).
    for (const item of enter) {
      const made = enterMark(deps, item, placed.view, placed.dial);
      if (!made) continue;
      marks.set(item.id, made.anchor);
      if (made.box) measureMark(made.box);
    }
  });
}

/**
 * One mark, built mid-gesture for an item the ring has just passed over.
 *
 * Deliberately NOT a rebuild of the layer: a rebuild would re-cluster the tags
 * and re-merge the badges, which is pixel work against a camera that is still
 * moving, and it would drop the open mark's spoke on every frame. The settle
 * rebuilds properly, one frame later (§4.2); this is the „appears at once"
 * half and nothing more — which is why the mark is never drawn selected here.
 */
function enterMark(
  deps: LiveDeps,
  item: Item,
  view: ViewState,
  dial: boolean,
): { anchor: HTMLElement; box: HTMLElement | null } | null {
  const mark = marksFor([item], markContext(view, dial))[0];
  if (!mark) return null;
  const position = mark.kind === 'dot' ? mark.position : mark.item.position;
  if (!position) return null;
  const el = mark.kind === 'dot' ? dotElement() : markButton(mark.item, false, mark.dimmed, [item]);
  const anchor = document.createElement('div');
  anchor.className = 'circle__mark';
  anchor.dataset.kind = mark.kind;
  anchor.dataset.item = mark.id;
  anchor.dataset.lon = String(position.lon);
  anchor.dataset.lat = String(position.lat);
  const at = deps.project(position.lon, position.lat);
  anchor.dataset.x = String(at.x);
  anchor.dataset.y = String(at.y);
  anchor.style.transform = `translate(${at.x}px, ${at.y}px)`;
  anchor.append(el);
  deps.marks.append(anchor);
  // The anchor is what the layer holds; the BOX is what the clamp measures, and
  // a context dot has no box to nudge, so it needs none.
  return { anchor, box: mark.kind === 'dot' ? null : el };
}

/** What a layer is holding right now, by item — read once per gesture. */
function held(layer: HTMLElement): Map<string, HTMLElement> {
  const by = new Map<string, HTMLElement>();
  for (const el of layer.children as HTMLCollectionOf<HTMLElement>) {
    if (el.dataset.item) by.set(el.dataset.item, el);
  }
  return by;
}
